import { SvgIconComponent } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Chip, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { SxProps } from '@mui/material/styles';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { memo, ReactElement, useMemo } from 'react';
import { bloxValues } from 'themes/constants';
import { MM_DD_YY_HHMM } from 'utility/constants';
import { formatDate } from 'utility/functions';
import { getColorMix } from 'utility/styles';
import {
  LogViewerColDef,
  LogViewerColDefs,
  LogViewerLabels,
  LogViewerRow,
  LogViewerStatus,
} from './LogViewer';

type StatusConfig = {
  icon: SvgIconComponent;
  color: string;
  labelKey: keyof LogViewerLabels;
};

type GetStatusConfigArgs = {
  status: LogViewerStatus;
  theme: Theme;
};

function getStatusConfig({ status, theme }: GetStatusConfigArgs): StatusConfig {
  switch (status) {
    case 'success':
      return {
        icon: CheckCircleIcon,
        color: theme.palette.success.main,
        labelKey: 'statusSuccess',
      };
    case 'failed':
      return {
        icon: ErrorOutlineIcon,
        color: theme.palette.error.main,
        labelKey: 'statusFailed',
      };
    case 'warning':
      return {
        icon: WarningAmberIcon,
        color: theme.palette.warning.main,
        labelKey: 'statusWarning',
      };
    case 'info':
      return {
        icon: InfoOutlinedIcon,
        color: theme.palette.info.main,
        labelKey: 'statusInfo',
      };
    case 'unknown':
      return {
        icon: HelpOutlineIcon,
        color: theme.palette.text.secondary,
        labelKey: 'statusUnknown',
      };
  }
}

type LogStatusChipProps = {
  status: LogViewerStatus;
  labels: LogViewerLabels;
};

/**
 * Status chip for row
 **/
export const LogStatusChipRow = memo(({ status, labels }: LogStatusChipProps) => {
  const theme = useTheme();
  const config = getStatusConfig({ status, theme });
  const Icon = config.icon;
  return (
    <LogStatusChip
      dataTestId={`log-viewer-status-${status}`}
      label={labels[config.labelKey]}
      color={config.color}
      icon={<Icon />}
    />
  );
});
LogStatusChipRow.displayName = 'LogStatusChipRow';

/**
 * Base Status Chip
 */
export type StatusChipProps = {
  label: string;
  color: string;
  icon?: ReactElement;
  propSx?: SxProps<Theme>;
  dataTestId?: string;
};
export const LogStatusChip = memo(
  ({ label, color, icon, propSx, dataTestId = 'log-status-chip' }: StatusChipProps) => {
    const theme = useTheme();

    const chipSx: SxProps<Theme> = useMemo(() => {
      const isDarkMode = theme?.palette?.mode === 'dark';
      const blackWhite = isDarkMode
        ? theme.palette.common.black
        : theme.palette.common.white;
      return {
        color: color,
        borderColor: color,
        bgcolor: getColorMix(color, 10, blackWhite, 'srgb'),
        fontSize: bloxValues.smallFontSizeRem,
        fontWeight: bloxValues.fontWeightMedium,
        px: 0.5,
        textTransform: 'capitalize',
        '.MuiChip-icon': {
          color: 'inherit',
          fontSize: bloxValues.regularFontSizeRem,
        },
        ...(typeof propSx === 'function' ? propSx(theme) : propSx),
      };
    }, [color, propSx, theme]);

    return (
      <Chip
        data-testid={dataTestId}
        icon={icon}
        label={label}
        size="small"
        variant="filled"
        sx={chipSx}
      />
    );
  },
);
LogStatusChip.displayName = 'LogStatusChip';

/**
 * Column-def getter (mirrors `getGridColumnDefs`): Time / Status / Driver /
 * Message. Columns are not sortable — the caller supplies pre-ordered rows.
 */
type GetLogViewerColumnsArgs = {
  labels: LogViewerLabels;
  siteTimeZone?: string;
  showDriver: boolean;
};

export function getLogViewerColumns({
  labels,
  siteTimeZone,
  showDriver,
}: GetLogViewerColumnsArgs): LogViewerColDefs {
  const timeColumn: LogViewerColDef = {
    field: 'time',
    headerName: labels.columnTime,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams<LogViewerRow>) => {
      return formatDate({
        date: params.row.time ?? undefined,
        format: MM_DD_YY_HHMM,
        shiftTz: siteTimeZone,
      });
    },
  };

  const statusColumn: LogViewerColDef = {
    field: 'status',
    headerName: labels.columnStatus,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams<LogViewerRow>) => {
      return (
        <LogStatusChipRow
          status={params.row.status}
          labels={labels}
        />
      );
    },
  };

  const messageColumn: LogViewerColDef = {
    field: 'message',
    headerName: labels.columnMessage,
    flex: 4,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams<LogViewerRow>) => {
      const message = params.row.message;
      if (!message) {
        return '';
      }
      return (
        <Tooltip
          title={message}
          enterDelay={300}
          enterNextDelay={300}
        >
          <Typography
            component="span"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {message}
          </Typography>
        </Tooltip>
      );
    },
  };

  const driverColumn: LogViewerColDef = {
    field: 'driver',
    headerName: labels.columnDriver,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams<LogViewerRow>) => {
      return params.row.driver;
    },
  };

  const baseCols: LogViewerColDefs = [timeColumn, statusColumn, messageColumn];

  if (showDriver) {
    baseCols.push(driverColumn);
  }

  return baseCols;
}
